import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { realTimeNotificationService } from '../services/realTimeNotificationService';
import { notificationService } from '../services/notificationService';

// Action types
const NOTIFICATION_ACTIONS = {
    SET_LOADING: 'SET_LOADING',
    SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
    ADD_NOTIFICATION: 'ADD_NOTIFICATION',
    UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
    REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
    SET_UNREAD_COUNT: 'SET_UNREAD_COUNT',
    UPDATE_UNREAD_COUNT: 'UPDATE_UNREAD_COUNT',
    SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
    SET_USER_ID: 'SET_USER_ID',
    SET_ERROR: 'SET_ERROR',
    CLEAR_ERROR: 'CLEAR_ERROR'
};

// Initial state
const initialState = {
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    isConnected: false,
    error: null,
    userId: null
};

// Reducer
const notificationReducer = (state, action) => {
    switch (action.type) {
        case NOTIFICATION_ACTIONS.SET_LOADING:
            return {
                ...state,
                isLoading: action.payload
            };

        case NOTIFICATION_ACTIONS.SET_NOTIFICATIONS:
            return {
                ...state,
                notifications: Array.isArray(action.payload) ? action.payload : [],
                isLoading: false
            };

        case NOTIFICATION_ACTIONS.ADD_NOTIFICATION:
            const newNotification = action.payload;
            const isUnread = newNotification.status !== 'read' && newNotification.status !== 'acknowledged';
            const currentNotifications = Array.isArray(state.notifications) ? state.notifications : [];
            
            return {
                ...state,
                notifications: [newNotification, ...currentNotifications.slice(0, 99)], // Keep last 100
                unreadCount: isUnread ? state.unreadCount + 1 : state.unreadCount
            };

        case NOTIFICATION_ACTIONS.UPDATE_NOTIFICATION:
            const currentNotificationsForUpdate = Array.isArray(state.notifications) ? state.notifications : [];
            const updatedNotifications = currentNotificationsForUpdate.map(notification =>
                notification.notification_id === action.payload.id
                    ? { ...notification, ...action.payload.updates }
                    : notification
            );
            
            // Recalculate unread count
            const unreadCount = updatedNotifications.filter(n => 
                n.status !== 'read' && n.status !== 'acknowledged'
            ).length;

            return {
                ...state,
                notifications: updatedNotifications,
                unreadCount
            };

        case NOTIFICATION_ACTIONS.REMOVE_NOTIFICATION:
            const currentNotificationsForRemove = Array.isArray(state.notifications) ? state.notifications : [];
            const filteredNotifications = currentNotificationsForRemove.filter(
                notification => notification.notification_id !== action.payload
            );
            
            // Recalculate unread count
            const newUnreadCount = filteredNotifications.filter(n => 
                n.status !== 'read' && n.status !== 'acknowledged'
            ).length;

            return {
                ...state,
                notifications: filteredNotifications,
                unreadCount: newUnreadCount
            };

        case NOTIFICATION_ACTIONS.SET_UNREAD_COUNT:
            return {
                ...state,
                unreadCount: action.payload
            };

        case NOTIFICATION_ACTIONS.UPDATE_UNREAD_COUNT:
            return {
                ...state,
                unreadCount: Math.max(0, state.unreadCount + action.payload)
            };

        case NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS:
            return {
                ...state,
                isConnected: action.payload
            };

        case NOTIFICATION_ACTIONS.SET_USER_ID:
            return {
                ...state,
                userId: action.payload
            };

        case NOTIFICATION_ACTIONS.SET_ERROR:
            return {
                ...state,
                error: action.payload,
                isLoading: false
            };

        case NOTIFICATION_ACTIONS.CLEAR_ERROR:
            return {
                ...state,
                error: null
            };

        default:
            return state;
    }
};

// Create context
const NotificationContext = createContext();

// Provider component
export const NotificationProvider = ({ children }) => {
    const [state, dispatch] = useReducer(notificationReducer, initialState);

    // Initialize notifications
    const initializeNotifications = async (userId) => {
        if (!userId) {
            console.log('No user ID provided, skipping notification initialization');
            return;
        }

        // Check if already initialized for this user and connected
        if (state.userId === userId && state.isConnected) {
            console.log('Notifications already initialized and connected for user:', userId);
            return;
        }

        // Check if user is authenticated
        const isAuthenticated = realTimeNotificationService.isUserAuthenticated();
        if (!isAuthenticated) {
            console.log('User not authenticated, skipping notification initialization');
            dispatch({ type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS, payload: false });
            return;
        }

        // Check if we're already in the process of initializing
        if (state.isLoading) {
            console.log('Notifications already initializing, skipping duplicate call');
            return;
        }

        try {
            dispatch({ type: NOTIFICATION_ACTIONS.SET_LOADING, payload: true });
            dispatch({ type: NOTIFICATION_ACTIONS.SET_USER_ID, payload: userId });

            // Only disconnect and reconnect if user ID changed or not connected
            if (state.userId !== userId || !state.isConnected) {
                // Disconnect any existing connection first
                realTimeNotificationService.disconnect();
                
                // Load initial notifications
                const response = await notificationService.getNotifications({
                    recipient_id: userId,
                    page: 1,
                    page_size: 50,
                    sort_by: 'created_at',
                    sort_order: 'desc'
                });

                if (response.success && response.data) {
                    const notifications = response.data.notifications || [];
                    dispatch({ type: NOTIFICATION_ACTIONS.SET_NOTIFICATIONS, payload: notifications });

                    // Set unread count
                    const unreadCount = response.data.total_unread || 
                        notifications.filter(n => n.status !== 'read' && n.status !== 'acknowledged').length;
                    dispatch({ type: NOTIFICATION_ACTIONS.SET_UNREAD_COUNT, payload: unreadCount });
                }

                // Connect to real-time service
                await realTimeNotificationService.connect(userId);

                // Set up event listeners (only if not already set up)
                realTimeNotificationService.on('notification', (notification) => {
                    console.log('New notification received:', notification);
                    dispatch({ type: NOTIFICATION_ACTIONS.ADD_NOTIFICATION, payload: notification });
                    
                    // Update unread count immediately
                    dispatch({ 
                        type: NOTIFICATION_ACTIONS.SET_UNREAD_COUNT, 
                        payload: (state.unreadCount || 0) + 1 
                    });
                });

                realTimeNotificationService.on('unreadCountChanged', (count) => {
                    console.log('Unread count changed:', count);
                    dispatch({ 
                        type: NOTIFICATION_ACTIONS.SET_UNREAD_COUNT, 
                        payload: count 
                    });
                });

                // Listen to connection status events
                realTimeNotificationService.on('connection_status', (status) => {
                    dispatch({ type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS, payload: status.isConnected });
                });

                realTimeNotificationService.on('connection_lost', (data) => {
                    dispatch({ type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS, payload: false });
                });

                // Monitor connection status as fallback (less frequent)
                const connectionInterval = setInterval(() => {
                    const status = realTimeNotificationService.getConnectionStatus();
                    dispatch({ type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS, payload: status.isConnected });
                }, 30000); // Check every 30 seconds instead of 10

                return () => {
                    clearInterval(connectionInterval);
                    realTimeNotificationService.off('notification');
                    realTimeNotificationService.off('unreadCountChanged');
                    realTimeNotificationService.off('connection_status');
                    realTimeNotificationService.off('connection_lost');
                    realTimeNotificationService.disconnect();
                };
            }

        } catch (error) {
            console.error('Error initializing notifications:', error);
            dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
        }
    };

    // Actions
    const actions = {
        initializeNotifications,

        async retryConnection() {
            if (state.userId) {
                await realTimeNotificationService.retryConnection();
            }
        },

        isAuthenticated() {
            return realTimeNotificationService.isUserAuthenticated();
        },

        async loadNotifications(userId, params = {}) {
            try {
                dispatch({ type: NOTIFICATION_ACTIONS.SET_LOADING, payload: true });

                const response = await notificationService.getNotifications({
                    recipient_id: userId,
                    page: 1,
                    page_size: 50,
                    sort_by: 'created_at',
                    sort_order: 'desc',
                    ...params
                });

                if (response.success && response.data) {
                    dispatch({ 
                        type: NOTIFICATION_ACTIONS.SET_NOTIFICATIONS, 
                        payload: response.data.notifications || [] 
                    });
                }
            } catch (error) {
                console.error('Error loading notifications:', error);
                dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
            }
        },

        async markAsRead(notificationId) {
            const MAX_RETRIES = 2;
            let attempt = 0;

            while (attempt < MAX_RETRIES) {
                try {
                    const response = await notificationService.markNotificationRead(notificationId);

                    if (response.success) {
                        dispatch({
                            type: NOTIFICATION_ACTIONS.UPDATE_NOTIFICATION,
                            payload: {
                                id: notificationId,
                                updates: {
                                    status: 'read',
                                    read_at: new Date().toISOString()
                                }
                            }
                        });
                        console.log(`✅ Successfully marked notification ${notificationId} as read (attempt ${attempt + 1})`);
                        return true;
                    }

                    // If we got a response but not successful, don't retry
                    console.error(`Mark as read failed: ${response.message}`);
                    return false;

                } catch (error) {
                    attempt++;

                    if (attempt >= MAX_RETRIES) {
                        console.error(`Error marking notification as read after ${MAX_RETRIES} attempts:`, error);
                        dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
                        return false;
                    }

                    // Wait before retry (exponential backoff)
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                    console.log(`Retrying mark as read (attempt ${attempt + 1}/${MAX_RETRIES})...`);
                }
            }

            return false;
        },

        async markAllAsRead(userId) {
            try {
                const currentNotifications = Array.isArray(state.notifications) ? state.notifications : [];
                const unreadNotifications = currentNotifications.filter(
                    n => n.status !== 'read' && n.status !== 'acknowledged'
                );

                if (unreadNotifications.length > 0) {
                    const response = await notificationService.markNotificationsRead({
                        notification_ids: unreadNotifications.map(n => n.notification_id)
                    });

                    if (response.success) {
                        // Update all unread notifications to read
                        unreadNotifications.forEach(notification => {
                            dispatch({
                                type: NOTIFICATION_ACTIONS.UPDATE_NOTIFICATION,
                                payload: {
                                    id: notification.notification_id,
                                    updates: { 
                                        status: 'read',
                                        read_at: new Date().toISOString()
                                    }
                                }
                            });
                        });
                        return true;
                    }
                }
                return false;
            } catch (error) {
                console.error('Error marking all notifications as read:', error);
                dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
                return false;
            }
        },

        async deleteNotification(notificationId) {
            try {
                const response = await notificationService.deleteNotification(notificationId);
                
                if (response.success) {
                    dispatch({
                        type: NOTIFICATION_ACTIONS.REMOVE_NOTIFICATION,
                        payload: notificationId
                    });
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Error deleting notification:', error);
                dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
                return false;
            }
        },

        clearError() {
            dispatch({ type: NOTIFICATION_ACTIONS.CLEAR_ERROR });
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            realTimeNotificationService.disconnect();
        };
    }, []);

    const value = {
        ...state,
        ...actions
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

// Hook to use notification context
export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export default NotificationContext;